import MemberRoutes from '@/router/member';
import PointsRoutes from '@/router/points';
import RegisterRoutes from '@/router/register';
import CompanyRoutes from '@/router/company';
import StatisticsRoutes from '@/router/statistics';
import pinia from '@/store/index';
import useUserStore from '@/store/user-store';
import {RoleE} from '@/types/user-types';
import * as Sentry from '@sentry/vue';
import {createRouter, createWebHistory} from 'vue-router';
import {
    BUSINESS_TRIP_ANALYSIS,
    COST_REDUCTION_ANALYSIS,
} from '@/types/statistics/constants';
import {getDefaultQueryParams as getReservationHistoryDefaultQueryParams} from '@/filters/reservationHistoryFilterConfig';

let nextPath = '';
const DEFAULT_TITLE = '여기어때 비즈니스';

const router = createRouter({
    history: createWebHistory(),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    scrollBehavior(to, from, savedPosition) {
        return {top: 0};
    },
    routes: [
        {
            path: '/',
            redirect: () => {
                return {
                    name: 'Reservation',
                    query: getReservationHistoryDefaultQueryParams(),
                };
            },
        },
        {
            // 업무 예약 내역
            path: '/reservation',
            name: 'Reservation',
            meta: {
                title: '업무 예약 내역',
            },
            components: {
                default: () => import('@/views/Reservation.vue'),
                NavBar: () => import('@/components/common/layout/NavBar.vue'),
                SideBar: () => import('@/components/common/layout/SideBar.vue'),
            },
        },
        ...PointsRoutes,
        ...MemberRoutes,
        ...RegisterRoutes,
        {
            // 제휴 수수료 내역
            path: '/commission',
            name: 'Commission',
            meta: {
                title: '제휴 수수료 내역',
            },
            components: {
                default: () => import('@/views/Commission.vue'),
                NavBar: () => import('@/components/common/layout/NavBar.vue'),
                SideBar: () => import('@/components/common/layout/SideBar.vue'),
            },
        },
        ...StatisticsRoutes,
        {
            // 출장 숙박비 규정
            path: '/business-trip/policy',
            name: 'BusinessTripPolicy',
            meta: {
                title: '출장 숙박비 규정',
            },
            components: {
                default: () => import('@/views/BusinessTripPolicy.vue'),
                NavBar: () => import('@/components/common/layout/NavBar.vue'),
                SideBar: () => import('@/components/common/layout/SideBar.vue'),
            },
        },
        ...CompanyRoutes,
        {
            // 내 정보
            path: '/user/info',
            name: 'UserInfo',
            meta: {
                title: '내 정보',
            },
            components: {
                default: () => import('@/views/UserInfo.vue'),
                NavBar: () => import('@/components/common/layout/NavBar.vue'),
                SideBar: () => import('@/components/common/layout/SideBar.vue'),
            },
        },
        {
            path: '/401',
            name: 'NotAllowed',
            meta: {
                title: '권한이 없습니다',
            },
            components: {
                default: () => import('@/views/NotAllowed.vue'),
            },
        },
        {
            // 로그인
            path: '/login',
            name: 'Login',
            meta: {
                title: '로그인',
            },
            components: {
                default: () => import('@/views/Login.vue'),
            },
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/404',
        },
        {
            path: '/404',
            name: 'NotFound',
            meta: {
                title: '요청하신 페이지를 찾을 수 없습니다',
            },
            components: {
                default: () => import('@/views/NotFound.vue'),
            },
        },
    ],
});

// FIXME: 로직이 너무 길다
router.beforeEach(async (to, from, next) => {
    nextPath = to.fullPath;
    const arrivalName = to.name as string;
    const registerRoutes = [
        // NOTE: ROLE_1: 총관리자
        'RegisterBeforeInvitation',
        'RegisterAfterInvitation',
        'RegisterSuccess',
        'DistinctLinkInvitation',
        'LegacyDistinctLinkInvitation',
        'DownloadAppAfterRegister',
        'ThirdPartyAgreement',
    ];
    const notAllowedRole2Routes = [
        // NOTE: ROLE_2 (관리자) 등급은 접근 불가능한 route들
        // NOTE: ROLE_2 (관리자): 업무 예약 내역, 통계 보고서, 회사 정보, 포인트 계약 정보만 접근 가능
        'PointRequestHistory',
        'PointTargetList',
        'MemberList',
        'MemberInvitation',
        'MemberIndividualRegister',
        'MemberBulkRegister',
        'Commission',
    ];
    const notAllowedRole3Routes = [
        // NOTE: ROLE_3 (구성원) 등급은 접근 불가능한 route들
        // NOTE: ROLE_3 (구성원): 업무 예약 내역, 회사 정보만 접근 가능
        ...notAllowedRole2Routes,
        'PointContractInfo',
        COST_REDUCTION_ANALYSIS,
        BUSINESS_TRIP_ANALYSIS,
    ];

    const user = useUserStore(pinia);
    await user.checkAuth();

    if (user.isLogin) {
        // NOTE: 기존 redirect 정책 유지
        if (arrivalName === 'Login') {
            next({
                name: 'Reservation',
            });
            return;
        }

        // NOTE: 기존 redirect 정책 유지
        if (registerRoutes.includes(arrivalName)) {
            next({name: 'LogoutBeforeRegister'});
            return;
        }

        /**
         * NOTE: ROLE_2(관리자) 등급이 접근할 수 없는 Route인 경우, 401 페이지로 push
         */
        if (
            user.getMyInfo.role === RoleE.ReservationManager &&
            notAllowedRole2Routes.includes(arrivalName)
        ) {
            next({name: 'NotAllowed'});
            return;
        }

        /**
         * NOTE: ROLE_3(구성원) 등급이 접근할 수 없는 Route인 경우, 401 페이지로 push
         */
        if (
            user.getMyInfo.role === RoleE.BusinessMember &&
            notAllowedRole3Routes.includes(arrivalName)
        ) {
            next({name: 'NotAllowed'});
            return;
        }

        /**
         * NOTE: 포인트 계약 정보: ROLE_1(총관리자) or ROLE_2(관리자) 등급이되, 포인트 계약 정보가 있는 계정만 접근 가능
         * ROLE_1 or ROLE_2인지 체크하는 로직은 상단 if문에 존재하므로,
         * 해당 if문에서는 포인트 계약 정보 유무만 체크함
         */
        if (
            arrivalName === 'PointContractInfo' &&
            !user.myInfo?.hasPointContract
        ) {
            next({name: 'NotAllowed'});
            return;
        }

        next();
    } else {
        // NOTE: 무한 라우팅 방지
        if (['Login', ...registerRoutes].includes(arrivalName)) {
            next();
            return;
        }

        next({name: 'Login'});
    }
});

/**
 * title 설정
 */
router.afterEach((to) => {
    // const user = useUserStore(pinia);
    // console.log(user.getMyInfo.hasPointContract)

    const ENV = import.meta.env.VITE_APP_ENVIRONMENT as
        | 'dev'
        | 'stage'
        | 'release';

    const title = to.meta.title
        ? `${to.meta.title} | ${DEFAULT_TITLE}`
        : DEFAULT_TITLE;

    document.title = ENV !== 'release' ? `[${ENV}] ${title}` : title;
});

router.onError((error) => {
    if (error.message.includes('Failed to fetch dynamically imported module')) {
        window.location.href = nextPath || location.href;
    }

    Sentry.captureException(error, {
        level: 'error',
        extra: {
            name: 'Router onError',
            from: 'src/router/index.ts',
        },
    });
});

export default router;
